import { useCallback, useEffect, useMemo, useState } from 'react';
import { pdApi } from '../api';
import './ScoringQueue.css';
import './ScoringAuditTrail.css';

/**
 * Opportunities → Audit Trail.
 *
 * OWNER-DIRECTED, 2026-08-10 — NOT a port. AI Readiness has no equivalent screen, so there
 * is nothing to clone; this is new work built to the brief, and it borrows the scoring
 * screens' visual language (`ScoringQueue.css`) so it belongs to the same family rather
 * than introducing a fourth look.
 *
 * The brief, in his words: *"Every single time the scoring has changed ... there was a value
 * and then someone else re-recorded it, we want to capture the date time who did that change
 * and what was the value before and after the change ... only for rescoring, and remember
 * rescoring can only be where the answer existed, and then it was basically changed to
 * another type of answer."*
 *
 * ── EVERY ROW HERE IS A REAL CHANGE ────────────────────────────────────────────────────
 *
 * Three things are deliberately absent, and their absence is the feature:
 *
 *   · first-time answers        answering is not re-scoring
 *   · re-saving the same answer  nothing changed
 *   · unanswered questions       never had a value to change
 *
 * The database enforces it too — `opportunity_score_audit` carries a CHECK that refuses
 * `old_answer_value = new_answer_value` — so this screen cannot fill with noise even if a
 * future caller forgets to compare before writing.
 *
 * ── THE WORDING IS HISTORICAL, NOT CURRENT ─────────────────────────────────────────────
 *
 * Question text and both answer wordings are read from the AUDIT ROW, which snapshot them
 * when the change happened. They are not joined from `questions` / `answer_choices`, because
 * a framework's wording can be edited afterwards and a trail that silently re-words itself
 * is worse than no trail at all.
 */

type Entry = Record<string, unknown>;

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** Date AND time — "date time who did that change" was the brief. */
const stamp = (v: unknown) =>
  !v
    ? ''
    : new Date(String(v)).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

/** The same 1–5 palette the scoring screens use, so a score reads the same everywhere. */
const scoreColour = (n: number) =>
  n >= 4 ? '#4caf50' : n >= 3 ? '#2196f3' : n >= 2 ? '#ff9800' : '#f44336';

export function ScoringAuditTrail() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOpportunity, setFilterOpportunity] = useState('');
  const [filterWho, setFilterWho] = useState('');
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.scoring.auditTrail();
      setEntries(data.entries ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* Measured height — the shell never bounds its content area, so `100%` collapses and the
     window scrolls instead, taking the header with it. */
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const visible = useMemo(() => {
    const o = filterOpportunity.trim().toLowerCase();
    const w = filterWho.trim().toLowerCase();
    return entries.filter((e) => {
      if (o && !s(e.opportunity_id).toLowerCase().includes(o)) return false;
      if (w && !s(e.changed_by_name).toLowerCase().includes(w)) return false;
      return true;
    });
  }, [entries, filterOpportunity, filterWho]);

  return (
    <div
      className="sq-page"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="sq-fixed">
        <div className="sq-header">
          <div>
            <h1 className="sq-title">Audit Trail</h1>
            <p className="sq-subtitle">
              Every scoring answer that was changed after it had been given — who changed it,
              when, and from what to what
            </p>
          </div>
        </div>

        {entries.length > 0 ? (
          <div className="sq-filters">
            <div className="sq-filter">
              <label htmlFor="at-fo">Opportunity:</label>
              <input
                id="at-fo"
                type="text"
                placeholder="Filter..."
                value={filterOpportunity}
                onChange={(e) => setFilterOpportunity(e.target.value)}
              />
            </div>
            <div className="sq-filter">
              <label htmlFor="at-fw">Changed by:</label>
              <input
                id="at-fw"
                type="text"
                placeholder="Filter..."
                value={filterWho}
                onChange={(e) => setFilterWho(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="sq-clear"
              onClick={() => {
                setFilterOpportunity('');
                setFilterWho('');
              }}
            >
              Clear
            </button>
            <span className="sq-count">
              {visible.length} of {entries.length} changes
            </span>
          </div>
        ) : null}
      </div>

      <div className="sq-body">
        {error ? <div className="sq-error">{error}</div> : null}

        <div className="sq-card">
          {loading ? (
            <div className="sq-empty">
              <p>Loading…</p>
            </div>
          ) : entries.length === 0 ? (
            /*
             * An EMPTY trail is the normal, healthy state — it means no answer has ever been
             * overwritten. The wording says so, rather than implying something is missing.
             */
            <div className="sq-empty">
              <div className="sq-empty-icon">🗂️</div>
              <h3>No Scoring Changes Recorded</h3>
              <p>
                An entry appears here only when an answer that had already been given is
                changed to a different one. First-time answers are not changes.
              </p>
            </div>
          ) : (
            <>
              <div className="sq-thead-wrap">
                <table className="sq-table">
                  <thead>
                    <tr>
                      <th style={{ width: 150 }}>When</th>
                      <th style={{ width: 140 }}>Changed by</th>
                      <th style={{ width: 130 }}>Opportunity</th>
                      <th style={{ width: 300 }}>Question</th>
                      <th style={{ width: 230 }}>Before</th>
                      <th style={{ width: 230 }}>After</th>
                      <th style={{ width: 80, textAlign: 'center' }}>Source</th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="sq-tbody-wrap">
                <table className="sq-table">
                  <tbody>
                    {visible.map((e) => (
                      <tr key={s(e.id)} className="at-row">
                        <td className="sq-cell-muted" style={{ width: 150, fontSize: 11 }}>
                          {stamp(e.changed_at)}
                        </td>
                        <td style={{ width: 140, fontSize: 11 }}>{s(e.changed_by_name) || '—'}</td>
                        <td style={{ width: 130, fontSize: 11 }}>
                          <strong>{s(e.opportunity_id)}</strong>
                          {e.project_id ? (
                            <div className="at-sub">{s(e.project_id)}</div>
                          ) : null}
                        </td>
                        <td style={{ width: 300, fontSize: 11 }}>
                          <div className="at-question">{s(e.question_text) || `#${s(e.question_id)}`}</div>
                        </td>

                        {/* Before / After — score chip plus the wording as it read THEN. */}
                        <td style={{ width: 230, fontSize: 11 }}>
                          <div className="at-answer">
                            <span
                              className="at-score"
                              style={{ backgroundColor: scoreColour(Number(e.old_answer_value)) }}
                            >
                              {s(e.old_answer_value)}
                            </span>
                            <span className="at-answer-text at-answer-text--old">
                              {s(e.old_choice_text)}
                            </span>
                          </div>
                        </td>
                        <td style={{ width: 230, fontSize: 11 }}>
                          <div className="at-answer">
                            <span
                              className="at-score"
                              style={{ backgroundColor: scoreColour(Number(e.new_answer_value)) }}
                            >
                              {s(e.new_answer_value)}
                            </span>
                            <span className="at-answer-text">{s(e.new_choice_text)}</span>
                          </div>
                        </td>

                        <td className="sq-cell-center" style={{ width: 80 }}>
                          <span className="at-source">{s(e.change_source)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
